/* eslint-disable no-console */
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DataRepository, WorkspaceDatabaseKeys } from '@database/workspace';
import { TranslateService } from '@ngx-translate/core';
import { ColumnModel, ToastDuration, ToastService } from '@shared';
import { firstValueFrom, Subscription, filter } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SamplesService } from '../../../business-layer/services';
import { FormControlFactoryService } from '../../factories';
import { FormControlTypeProviderService } from '../../factories/form-control-type-provider';
import { FormControlBase, FormValueModel, ITFormControlType } from '../../models';
import {
	DropdownDataSourceProvider,
	FormBuilderService,
	FormControlsBuilderService,
	NormalizeDatabaseValuesService,
	RecalculateFormulaService,
	UnsavedChangesService
} from '../../services';
import { CrossTableValidationsBuilderService, IntervalService } from '../../validations';
import { AsyncValidatorsOrchestratorService } from '../../validations/async-validators';

@Component({
	selector: 'app-dynamic-form',
	templateUrl: './dynamic-form.component.html',
	styleUrls: ['./dynamic-form.component.scss'],
	providers: [
		CrossTableValidationsBuilderService,
		FormControlsBuilderService,
		FormBuilderService,
		IntervalService,
		FormControlFactoryService,
		FormControlTypeProviderService,
		AsyncValidatorsOrchestratorService,
		NormalizeDatabaseValuesService,
		DropdownDataSourceProvider,
		SamplesService
	]
})
export class DynamicFormComponent implements OnInit, OnDestroy {
	@Input() public tableName: string;
	@Input() public primaryKeys: string[];
	@Output() public scrollTop = new EventEmitter<boolean>();
	@Output() public submitFormValue = new EventEmitter<FormValueModel>();

	public form: UntypedFormGroup;
	public dynamicFormControls: FormControlBase<ITFormControlType>[];

	private subscriptions: Subscription[] = [];

	public constructor(
		private formBuilderService: FormBuilderService,
		private formControlBuilderService: FormControlsBuilderService,
		private dataRepository: DataRepository,
		private toastService: ToastService,
		private translateService: TranslateService,
		private unsavedChangesService: UnsavedChangesService,
		private sampleService: SamplesService,
		private recalculateFormulaService: RecalculateFormulaService
	) { }

	public ngOnInit(): void {
		this.subscriptions.push(
			this.dataRepository
				.getColumnsMetadata$(this.tableName)
				.pipe(
					switchMap((columnsMetadata: ColumnModel[]) =>
						this.formControlBuilderService.build$(columnsMetadata)
					)
				)
				.subscribe((dynFormCtrls: FormControlBase<ITFormControlType>[]) => {
					this.dynamicFormControls = dynFormCtrls;
					this.form = this.formBuilderService.buildFormGroup(dynFormCtrls);

					this.subscriptions.push(
						this.form.valueChanges.subscribe(() => {
							this.unsavedChangesService.setHasChanges(this.form.dirty);
							this.recalculateFormulaService.refreshFormulaValue(true);
						})
					);
				})
		);

	}

	public async specialSubmit(): Promise<void> {
		if (this.form.value.depth_from && this.form.value.depth_to) {
			this.form.value.length = +this.form.value.depth_to - +this.form.value.depth_from;
		}

		if (this.form.status === 'PENDING') {
			this.scrollTop.emit(true);
			return await this.toastService.presentError(
				this.translateService.instant('formValidations.validationInProgress'),
				ToastDuration.Fast
			);
		}

		if (this.form.invalid) {
			this.scrollTop.emit(true);
			return await this.toastService.presentError(
				this.translateService.instant('formValidations.formHasErrors'),
				ToastDuration.Fast
			);
		}

		const isQcSampleCodeDuplicate = await firstValueFrom(
			this.sampleService.isQcSampleCodeDuplicate$(
				this.form.value[WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCode],
				Number(this.form.value[WorkspaceDatabaseKeys.columns.depthFrom]),
				Number(this.form.value[WorkspaceDatabaseKeys.columns.depthTo])
			)
		);

		if (isQcSampleCodeDuplicate) {
			this.scrollTop.emit(true);
			return await this.toastService.presentError(
				this.translateService.instant('formValidations.uniqueQCRecordForAnOriginal'),
				ToastDuration.Fast
			);
		}

		this.submitFormValue.emit(this.form.value);
		this.unsavedChangesService.setHasChanges(false);
		this.recalculateFormulaService.refreshFormulaValue(false);
	}


	public async regularSubmit(): Promise<void> {
		if (this.form.status === 'PENDING') {
			this.scrollTop.emit(true);
			return await this.toastService.presentError(
				this.translateService.instant('formValidations.validationInProgress'),
				ToastDuration.Fast
			);
		}

		if (this.form.invalid) {
			this.scrollTop.emit(true);
			return await this.toastService.presentError(
				this.translateService.instant('formValidations.formHasErrors'),
				ToastDuration.Fast
			);
		}

		const isQcSampleCodeDuplicate = await firstValueFrom(
			this.sampleService.isQcSampleCodeDuplicate$(
				this.form.value[WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCode],
				Number(this.form.value[WorkspaceDatabaseKeys.columns.depthFrom]),
				Number(this.form.value[WorkspaceDatabaseKeys.columns.depthTo])
			)
		);

		if (isQcSampleCodeDuplicate) {
			this.scrollTop.emit(true);
			return await this.toastService.presentError(
				this.translateService.instant('formValidations.uniqueQCRecordForAnOriginal'),
				ToastDuration.Fast
			);
		}

		this.submitFormValue.emit(this.form.value);
		this.unsavedChangesService.setHasChanges(false);
		this.recalculateFormulaService.refreshFormulaValue(false);
	}

	public async onSubmit(): Promise<void> {
		this.form.markAllAsTouched();
		this.form.updateValueAndValidity();

		// Wait for async validators to complete
		await this.awaitFormReady(this.form);

		if (this.tableName === "HOLE_ASSAY_SAMPLE") {
			await this.specialSubmit();
		} else {
			await this.regularSubmit();
		}
	}

	public ngOnDestroy(): void {
		this.subscriptions.forEach((subscription) => {
			subscription.unsubscribe();
		});
	}

	private async awaitFormReady(form: UntypedFormGroup): Promise<void> {
		if (form.status !== 'PENDING') {
			return;
		}

		await firstValueFrom(
			form.statusChanges.pipe(
				filter(status => status !== 'PENDING')
			)
		);
	}
}
